// @flow

import React from 'react';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import i18n from 'app/common/lib/i18n';
import millisToMinutesAndSeconds from 'app/common/lib/util/millisToMinutesAndSeconds';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type StatsType } from 'app/common/react/components/closeup/PinAnalytics';
import { useExperiment } from 'app/common/react/components/experiments/useExperiment';
import { Box, Icon, Text } from 'gestalt';

function getPinStatsData(pin: Pin) {
  const { aggregated_pin_data: aggregatedPinData, creator_analytics: rootPinStats } = pin;
  const { creator_analytics: canonicalPinStats } = aggregatedPinData || {};

  const { isStoryPin } = getPinTypes(pin);

  // story pins should default to using root pin stats
  if (rootPinStats && (isStoryPin || !canonicalPinStats)) {
    return rootPinStats;
  }
  return canonicalPinStats;
}

type ValueFormat = 'TIME' | 'COUNT';

function renderValue(value: number, valueFormat: ValueFormat = 'COUNT') {
  if (Number.isInteger(value)) {
    return valueFormat === 'TIME' ? (
      millisToMinutesAndSeconds(value)
    ) : (
      <PrettyNumber value={value} shortform shortformMaximumFractionDigits={1} />
    );
  }

  return '-';
}

type ExtendedStats = {|
  ...StatsType,
  align: 'start' | 'center' | 'end',
  valueFormat?: ValueFormat,
|};

type Props = {|
  pin: Pin,
|};

export default function PinStats({ pin }: Props) {
  const { anyEnabled: show90Days } = useExperiment('px_pinstats_90_day_default_on_grid');
  const { isStoryPin, isVideo } = getPinTypes(pin);
  const stats = getPinStatsData(pin);

  if (!stats) {
    return null;
  }

  const statsList: Array<ExtendedStats> = isVideo
    ? [
        {
          iconType: 'play',
          displayText: i18n._('Video views', 'Label for views of a video pin on closeup page'),
          field: 'video_views',
          align: 'start',
        },
        {
          iconType: 'clock',
          displayText: i18n._('Avg. time', 'Label for average time of a video pin on closeup page'),
          field: 'video_average_time',
          align: 'center',
          valueFormat: 'TIME',
        },
        {
          iconType: 'angled-pin',
          displayText: i18n._('Saves', 'Label for saves of a video pin on closeup page'),
          field: 'save',
          align: 'end',
        },
      ]
    : [
        {
          iconType: 'eye',
          displayText: i18n._('Impressions', 'Label for impressions of a pin on closeup page'),
          field: 'impression',
          align: 'start',
        },
        {
          iconType: 'angled-pin',
          displayText: i18n._('Saves', 'Label for saves of a pin on closeup page'),
          field: 'save',
          align: 'center',
        },
        {
          iconType: 'arrow-up-right',
          displayText: i18n._('Link clicks', 'Label for click-throughs of a pin on closeup page'),
          field: 'clickthrough',
          iconSize: 10,
          align: 'end',
        },
      ];

  const defaultStatsProp = show90Days ? '90d' : '30d';
  const renderedStats =
    (isVideo || isStoryPin) && !!stats.all_time ? stats.all_time : stats[defaultStatsProp];

  if (!renderedStats || Object.keys(renderedStats).length === 0) {
    return null;
  }

  return (
    <Box display="flex">
      {statsList.map(({ align, displayText, field, iconType, iconSize = 12, valueFormat }) => (
        <Box alignItems="center" column={4} display="flex" justifyContent={align} key={field}>
          <Box display="inlineBlock" marginRight={1}>
            <Icon
              accessibilityLabel={displayText}
              color="darkGray"
              icon={iconType}
              size={iconSize}
            />
          </Box>
          <Text inline size="sm">
            {renderValue(renderedStats[field], valueFormat)}
          </Text>
        </Box>
      ))}
    </Box>
  );
}
